import { useState } from 'react';
import PropTypes from 'prop-types';
import gocardlessImage from './assets/gocardless.png';
import afterbanksImage from './assets/afterbanks.png';
import indexaCapitalImage from './assets/indexa.png';
import tellerImage from './assets/teller.png';
import stripeImage from './assets/stripe.png';
import wealthReaderImage from './assets/wealthreader.png';
import { formatList, htmlDecode } from 'Utils/formatters';
import { useTranslation } from 'react-i18next';
import useLocale from 'Hooks/useLocale';
import ProviderConnectorsSelectorItem from './ProviderConnectorsSelectorItem';
import { AnimatePresence, motion } from 'framer-motion';
import useConnectorsIncidents from 'Hooks/useConnectorsIncidents';
import { CONNECTOR_HEALTH_STATUSES } from 'Utils/constants';

export default function ProviderConnectionConnect(props) {
    const { connectors, onSelect, provider } = props;

    const { getConnectorHealth } = useConnectorsIncidents();
    const { t } = useTranslation('providers');
    const [showNotRecommended, setShowNotRecommended] = useState(false);

    const locale = useLocale();

    const CONNECTION_SERVICES = {
        afterbanks_psd2: {
            title: 'Open Banking (PSD2)',
            subtitle: `${t('via')} Arcopay`,
            image: afterbanksImage,
            hasTransactionsBalance: false,
        },
        nordigen: {
            title: 'Open Banking (PSD2)',
            subtitle: `${t('via')} GoCardLess`,
            image: gocardlessImage,
            hasTransactionsBalance: false,
        },
        afterbanks_v3: {
            title: t('direct-access'),
            subtitle: `${t('via')} Arcopay`,
            image: afterbanksImage,
            hasTransactionsBalance: true,
        },
        indexa_capital: {
            title: t('native-connection'),
            subtitle: `${t('via')} Indexa Capital`,
            image: indexaCapitalImage,
            hasTransactionsBalance: false,
        },
        wealth_reader: {
            title: t('direct-access'),
            subtitle: `${t('via')} Wealth Reader S.L.`,
            image: wealthReaderImage,
            hasTransactionsBalance: true,
        },
        teller: {
            title: t('direct-access'),
            subtitle: `${t('via')} Teller.io`,
            image: tellerImage,
            hasTransactionsBalance: true,
        },
        stripe: {
            title: t('direct-access'),
            subtitle: `${t('via')} Stripe Financial Connections`,
            image: stripeImage,
            hasTransactionsBalance: false,
        },
    };

    const PRODUCT_TRANSLATIONS = {
        checkings: t('checkings'),
        cards: t('cards'),
        loans: t('loans'),
        investments: t('investments'),
        others: t('others'),
    };

    const formatSyncProducts = (products) => {
        if (!products) return null;

        const translatedProducs = products
            .map((product) => PRODUCT_TRANSLATIONS[product])
            .filter(Boolean)
            .map((product) => product.toLocaleLowerCase());

        const formattedProducts = formatList(translatedProducs, { limit: 100, locale });

        return `${t('sync-product')} ${formattedProducts}`;
    };

    const expirationString = (connector) => {
        if (connector.update_delta === null) {
            return t('manual-sync');
        } else if (connector.average_expiration !== null) {
            return t('expires-every', { count: connector.average_expiration.toFixed(0) });
        } else {
            return null;
        }
    };

    const isBroken = (connector) => getConnectorHealth(connector).status === CONNECTOR_HEALTH_STATUSES.BROKEN;

    const connectorsFormatted = connectors
        .filter((connector) => typeof CONNECTION_SERVICES[connector.service] !== 'undefined')
        .sort((a, b) => {
            // Connectors of a bank that is down go last, so the user picks one that works.
            if (isBroken(a) !== isBroken(b)) return isBroken(a) ? 1 : -1;
            if (a.is_recommended) return -1;
            if (b.is_recommended) return 1;
            if (a.order === null) return 1;
            if (b.order === null) return -1;

            return a.order - b.order;
        })
        .map((connector) => {
            const connection = CONNECTION_SERVICES[connector.service];
            const health = getConnectorHealth(connector);
            const isUnavailable = health.status === CONNECTOR_HEALTH_STATUSES.BROKEN;

            return {
                ...connection,
                id: connector.id,
                isRecommended: connector.is_recommended && !isUnavailable,
                isUnavailable,
                unavailableMessage: health.message,
                hasProblems: health.status === CONNECTOR_HEALTH_STATUSES.WITH_PROBLEMS,
                syncProducts: formatSyncProducts(connector.available_products),
                expire: expirationString(connector),
                isBusiness: connector.is_business,
                connector,
            };
        });

    const recommendedConnectors = connectorsFormatted.filter((item) => item.isRecommended);
    const notRecommendedConnectors = connectorsFormatted.filter((item) => !item.isRecommended);

    const showStacked = recommendedConnectors.length > 0 && notRecommendedConnectors.length > 0;

    return (
        <div className="flex flex-col md:pt-8 lg:pt-16 items-center gap-2 pb-5 lg:pb-6">
            <div className="text-center flex flex-col">
                <img
                    src={provider.logo_url}
                    className="mx-auto md:mb-2 w-16 h-auto block md:w-24"
                    alt={provider.name}
                />

                <h2 className="mb-2 text-2xl md:text-3xl text-pretty font-semibold">
                    {t('connect-with')}
                    <br /> {provider.name}
                </h2>
                <div className="text-gray-500 text-lg text-balance">
                    {htmlDecode(t('what-connection-type-do-you-want-to-use-to-connect-with', { name: provider.name }))}
                </div>
            </div>

            <div className="py-4 w-full">
                {connectorsFormatted.length === 0 && (
                    <div className="text-center py-5 text-gray-500">{t('no-providers-connection-available')}</div>
                )}

                {showStacked && (
                    <>
                        <div className="grid gap-4 mb-4">
                            {recommendedConnectors.map((item) => {
                                return (
                                    <ProviderConnectorsSelectorItem
                                        key={item.id}
                                        onClick={() => onSelect(item.connector)}
                                        isBusiness={item.isBusiness}
                                        isRecommended={item.isRecommended}
                                        title={item.title}
                                        subtitle={item.subtitle}
                                        image={item.image}
                                        syncProducts={item.syncProducts}
                                        expire={item.expire}
                                        hasTransactionsBalance={item.hasTransactionsBalance}
                                        isUnavailable={item.isUnavailable}
                                        unavailableMessage={item.unavailableMessage}
                                        hasProblems={item.hasProblems}
                                    />
                                );
                            })}
                        </div>

                        {!showNotRecommended && (
                            <div className="flex justify-center">
                                <button
                                    type="button"
                                    className="text-center text-gray-500 text-sm bg-transparent rounded-md px-3 py-2 underline"
                                    onClick={() => setShowNotRecommended(!showNotRecommended)}
                                >
                                    {t('other-connections', { count: notRecommendedConnectors.length })}
                                </button>
                            </div>
                        )}

                        <AnimatePresence initial={false}>
                            {showNotRecommended && (
                                <motion.div
                                    key="content"
                                    initial="collapsed"
                                    animate="open"
                                    exit="collapsed"
                                    variants={{
                                        open: { height: 'auto', opacity: 1 },
                                        collapsed: { height: 0, opacity: 0 },
                                    }}
                                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                                    className="min-w-0 overflow-hidden pb-3"
                                >
                                    <div className="grid gap-4">
                                        {notRecommendedConnectors.map((item) => {
                                            return (
                                                <ProviderConnectorsSelectorItem
                                                    key={item.id}
                                                    onClick={() => onSelect(item.connector)}
                                                    isBusiness={item.isBusiness}
                                                    isRecommended={item.isRecommended}
                                                    title={item.title}
                                                    subtitle={item.subtitle}
                                                    image={item.image}
                                                    syncProducts={item.syncProducts}
                                                    expire={item.expire}
                                                    hasTransactionsBalance={item.hasTransactionsBalance}
                                                    isUnavailable={item.isUnavailable}
                                                    unavailableMessage={item.unavailableMessage}
                                                    hasProblems={item.hasProblems}
                                                />
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </>
                )}

                {!showStacked && (
                    <div className="grid gap-4">
                        {connectorsFormatted.map((item) => {
                            return (
                                <ProviderConnectorsSelectorItem
                                    key={item.id}
                                    onClick={() => onSelect(item.connector)}
                                    isBusiness={item.isBusiness}
                                    isRecommended={item.isRecommended}
                                    title={item.title}
                                    subtitle={item.subtitle}
                                    image={item.image}
                                    syncProducts={item.syncProducts}
                                    expire={item.expire}
                                    hasTransactionsBalance={item.hasTransactionsBalance}
                                    isUnavailable={item.isUnavailable}
                                    unavailableMessage={item.unavailableMessage}
                                    hasProblems={item.hasProblems}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
            <div className="mt-6 opacity-50 text-center text-xs text-gray-500 hidden">
                {t('remember-you-can-always-modify-the-connection-later-from-your-bank-settings')}
            </div>
        </div>
    );
}

ProviderConnectionConnect.propTypes = {
    connectors: PropTypes.array.isRequired,
    onSelect: PropTypes.func.isRequired,
    provider: PropTypes.object.isRequired,
};
